// src/components/PaystackCheckoutOneTime.tsx
import React, { useState } from "react";
import { PaystackButton } from "react-paystack";
import { auth, db } from "../firebase/firebase";
import { doc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import CustomAlert from "./CustomAlert";

// Make sure your tsconfig.json includes this, or else add a global .d.ts:
//   {
//     "compilerOptions": { …, "strict": true, "skipLibCheck": false, … }
//   }

export default function PaystackCheckoutOneTime() {
  // 1) Load the public key from env
  const publicKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;
  const navigate = useNavigate();
  const user = auth.currentUser;
  const [alerts, setAlerts] = useState<
    { id: number; message: string; type: "success" | "error" | "warning" }[]
  >([]);
  const [alertId, setAlertId] = useState(0);

  const showAlert = (
    message: string,
    type: "success" | "error" | "warning" = "error"
  ) => {
    const newAlert = { id: alertId, message, type };
    setAlerts((prev) => [...prev, newAlert]);
    setAlertId((prev) => prev + 1);
  };

  const closeAlert = (id: number) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  };

  // 2) If no publicKey, throw so we can see it immediately
  if (!publicKey) {
    throw new Error(
      "REACT_APP_PAYSTACK_PUBLIC_KEY is not defined. Please set it in your .env file."
    );
  }

  // 3) If user is not signed in, just render a “Sign up” button
  if (!user) {
    return (
      <button
        onClick={() => {
          alert("You must be signed in to purchase.");
          navigate("/signup");
        }}
        className="bg-teal-500 text-white px-4 py-2 rounded-lg hover:bg-teal-600 transition"
      >
        Sign up to purchase
      </button>
    );
  }

  // 4) Build the Paystack metadata—note the required custom_fields: []
  const metadata = {
    userId: user.uid,
    purchaseType: "one_time_pro",
    custom_fields: [] as {
      display_name: string;
      variable_name: string;
      value: string;
    }[],
  };

  // 5) onSuccess must be wrapped in try/catch so any thrown error is caught.
  const handleSuccess = async (reference: { reference: string }) => {
    try {
      // Write to Firestore that this user has paid once
      const userRef = doc(db, "users", user.uid);
      await setDoc(
        userRef,
        {
          subscriptionTier: "pro", // or however you track “paid” status
          lastReference: reference.reference,
          lastPaidAt: Date.now(),
        },
        { merge: true }
      );
      showAlert("🎉 Payment successful! You now have Pro access.");
      window.location.reload();
    } catch (err) {
      console.error("❌ Error writing to Firestore in onSuccess:", err);
      showAlert(
        "Payment succeeded, but we couldn’t update your account. Please contact support."
      );
    }
  };

  // 6) Build the props for <PaystackButton />
  const componentProps = {
    email: user.email || "", // react-paystack expects a non-empty string
    amount: 27500, // R275.00 → 27500 kobo
    currency: "ZAR",
    publicKey,
    metadata,
    text: "Upgrade for Pro Access - $15.00 ",
    onSuccess: handleSuccess,
    onClose: () => {
      alert("Transaction was not completed.");
    },
    style: {
      backgroundColor: "#059669", // Tailwind’s bg-teal-500 → #059669
      color: "white",
      padding: "0.5rem 1rem", // px-4 py-2
      borderRadius: "0.375rem", // rounded-lg
      cursor: "pointer",
    },
  };

  // 7) Return the Paystack button
  //    We wrap it in a try/catch guard in case
  //    PaystackButton’s constructor throws synchronously.
  try {
    return <PaystackButton {...componentProps} />;
  } catch (err) {
    console.error("❌ Failed to render PaystackButton:", err);
    return (
      <div className="p-4 bg-red-500 text-white">
        {alerts.map((alert) => (
          <CustomAlert
            key={alert.id}
            message={alert.message}
            type={alert.type}
            onClose={() => closeAlert(alert.id)}
          />
        ))}
        Unable to load payment form. Please try again later.
      </div>
    );
  }
}
